<template>
  <div class="space-y-2">
    <!-- Node/endpoint select -->
    <div>
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{
        tt('Zigbee endpoint', 'Končna točka Zigbee')
      }}</label>
      <select
        v-model="selectedEndpointId"
        class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        @change="onEndpointChange">
        <option value="">{{ tt('Select endpoint…', 'Izberite končno točko…') }}</option>
        <optgroup v-for="node in nodes" :key="node.ieeeAddress" :label="node.label">
          <option v-for="ep in node.endpoints" :key="ep.id" :value="ep.id">
            {{ tt('Endpoint', 'Končna točka') }} {{ ep.endpointNumber }}
          </option>
        </optgroup>
      </select>
    </div>

    <!-- Cluster/attribute picker -->
    <div v-if="selectedEndpointId && clusters.length > 0" class="space-y-2">
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{{
        tt('Cluster attribute', 'Atribut gruče')
      }}</label>
      <select
        v-model="selectedAttributeId"
        class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        @change="emitValue">
        <option value="">{{ tt('Select attribute…', 'Izberite atribut…') }}</option>
        <optgroup v-for="cluster in clusters" :key="cluster.id" :label="cluster.clusterName">
          <option v-for="attr in cluster.attributes" :key="attr.id" :value="attr.id">
            {{ attr.attributeName }}
            <template v-if="!attr.isWritable && referenceType === 'control'">
              {{ tt(' [read-only]', ' [samo za branje]') }}</template
            >
          </option>
        </optgroup>
      </select>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="text-xs text-gray-400 flex items-center gap-1">
      <span class="material-symbols-outlined text-sm animate-spin">progress_activity</span>
      {{ tt('Loading Zigbee data…', 'Nalaganje podatkov Zigbee…') }}
    </div>
    <div v-else-if="!loading && nodes.length === 0" class="text-xs text-gray-400">
      {{ tt('No Zigbee endpoints found for this integration.', 'Za to integracijo ni najdenih končnih točk Zigbee.') }}
    </div>

    <!-- Selected attribute summary -->
    <div
      v-if="selectedAttribute"
      class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700">
      <span class="material-symbols-outlined text-sm text-teal-500">hub</span>
      <div class="flex-1 min-w-0">
        <div class="text-xs font-medium text-gray-900 dark:text-white">{{ selectedAttribute.attributeName }}</div>
        <div class="text-xs text-gray-500 dark:text-gray-400 font-mono">{{ selectedAttribute.dataType }}</div>
      </div>
      <span
        :class="[
          'px-1.5 py-0.5 text-xs rounded',
          selectedAttribute.isWritable
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
        ]">
        {{ selectedAttribute.isWritable ? tt('writable', 'za pisanje') : tt('read-only', 'samo za branje') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

export interface ZigbeeAttributeRef {
  zigbeeAttributeId: string; // ZigbeeClusterAttribute.id (UUID)
  zigbeeEndpointId: string; // ZigbeeEndpoint.id (UUID)
  attributeName: string;
  clusterId: number;
  clusterName: string;
}

interface ZigbeeAttributeItem {
  id: string;
  attributeName: string;
  dataType: string;
  isWritable: boolean;
  clusterId: string; // UUID of cluster record
}

interface ZigbeeClusterItem {
  id: string;
  clusterId: number;
  clusterName: string;
  attributes: ZigbeeAttributeItem[];
  endpointId?: string | number;
}

interface ZigbeeEndpointEntry {
  id: string;
  endpointNumber: number;
}

interface ZigbeeNodeEntry {
  ieeeAddress: string;
  label: string;
  endpoints: ZigbeeEndpointEntry[];
}

interface Props {
  /** The hub stores the ids first and the picker fills the row in. */
  modelValue?: Partial<ZigbeeAttributeRef>;
  integrationId: string;
  referenceType: 'status' | 'control';
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: ZigbeeAttributeRef | undefined];
}>();

const loading = ref(false);
const nodes = ref<ZigbeeNodeEntry[]>([]);
const clusters = ref<ZigbeeClusterItem[]>([]);
const selectedEndpointId = ref('');
const selectedAttributeId = ref('');

const selectedAttribute = computed(() => {
  for (const c of clusters.value) {
    const a = c.attributes.find((a) => a.id === selectedAttributeId.value);
    if (a) return { ...a, clusterName: c.clusterName, clusterTypeId: c.clusterId };
  }
  return undefined;
});

async function loadData() {
  if (!props.integrationId) return;
  loading.value = true;
  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    nodes.value = Array.isArray(data?.zigbee?.nodes) ? data.zigbee.nodes : [];
  } catch (e) {
    console.error('[ZigbeeReferenceInput] load error:', e);
  } finally {
    loading.value = false;
  }
}

async function onEndpointChange() {
  selectedAttributeId.value = '';
  clusters.value = [];
  if (!selectedEndpointId.value) return;
  loading.value = true;
  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    const allClusters: ZigbeeClusterItem[] = Array.isArray(data?.zigbee?.clusters) ? data.zigbee.clusters : [];
    clusters.value = allClusters.filter((c) => c.endpointId === selectedEndpointId.value);
  } catch (e) {
    console.error('[ZigbeeReferenceInput] load clusters error:', e);
  } finally {
    loading.value = false;
  }
}

function emitValue() {
  if (!selectedEndpointId.value || !selectedAttributeId.value) {
    emit('update:modelValue', undefined);
    return;
  }
  const attr = selectedAttribute.value;
  if (!attr) {
    emit('update:modelValue', undefined);
    return;
  }
  emit('update:modelValue', {
    zigbeeAttributeId: attr.id,
    zigbeeEndpointId: selectedEndpointId.value,
    attributeName: attr.attributeName,
    clusterId: attr.clusterTypeId,
    clusterName: attr.clusterName,
  });
}

watch(
  () => props.modelValue,
  async (val) => {
    if (!val) {
      selectedEndpointId.value = '';
      selectedAttributeId.value = '';
      return;
    }
    await loadData();
    selectedEndpointId.value = val.zigbeeEndpointId ?? '';
    await onEndpointChange();
    selectedAttributeId.value = val.zigbeeAttributeId ?? '';
  },
  { immediate: true },
);

onMounted(() => {
  loadData();
});
</script>
